import React, { useState, useEffect } from 'react';
import { Plus, X, Edit2, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import CreateIDOTransactionModal from '../modals/CreateIDOTransactionModal';

const IDOTab = ({ clientId, onRefresh }) => {
  const { toast } = useAdminToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingHolding, setEditingHolding] = useState(null);

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getClientIDO(clientId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load IDO data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTx = async (txId) => {
    if (!window.confirm('Удалить транзакцию? Баланс будет скорректирован.')) return;
    try {
      await adminAPI.deleteClientIDOTransaction(clientId, txId);
      loadData();
      onRefresh();
    } catch (err) {
      toast.apiError(err);
    }
  };

  const handleUpdateHolding = async (asset, newQty) => {
    try {
      await adminAPI.updateClientIDOHolding(clientId, asset, newQty);
      loadData();
      onRefresh();
      setEditingHolding(null);
    } catch (err) {
      toast.apiError(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>Загрузка...</div>;
  }

  return (
    <div>
      {/* Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Баланс IDO</div>
          <div style={{ color: COLORS.primary, fontSize: '20px', fontWeight: '700' }}>
            ${data?.total_value?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div style={{
          padding: '16px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
        }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Позиций</div>
          <div style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700' }}>
            {data?.holdings?.length || 0}
          </div>
        </div>
      </div>

      {/* Holdings */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', margin: 0 }}>
            Токены IDO
          </h4>
        </div>
        
        {data?.holdings?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {data.holdings.map((h, idx) => (
              <div key={h.id || idx} style={{
                padding: '12px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: COLORS.background,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {h.project_logo || h.logo ? (
                        <img src={h.project_logo || h.logo} alt={h.asset} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '14px' }}>{h.asset?.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
                        {h.project_name || h.token_name || h.asset}
                      </div>
                      <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                        ${h.asset}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    {editingHolding === h.asset ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="number"
                          defaultValue={h.quantity}
                          id={`qty-${h.asset}`}
                          style={{
                            width: '80px',
                            padding: '6px 8px',
                            backgroundColor: COLORS.background,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '4px',
                            color: COLORS.text,
                            fontSize: '13px',
                          }}
                        />
                        <button
                          onClick={() => {
                            const val = parseFloat(document.getElementById(`qty-${h.asset}`).value);
                            if (!isNaN(val)) handleUpdateHolding(h.asset, val);
                          }}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: COLORS.success,
                            border: 'none',
                            borderRadius: '4px',
                            color: '#fff',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => setEditingHolding(null)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: COLORS.background,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '4px',
                            color: COLORS.textSecondary,
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '700' }}>
                          {h.quantity?.toLocaleString()}
                        </div>
                        <div style={{ color: COLORS.success, fontSize: '12px' }}>
                          ≈ ${h.current_value?.toFixed(2) || '0.00'}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {editingHolding !== h.asset && (
                    <button
                      onClick={() => setEditingHolding(h.asset)}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        color: COLORS.textSecondary,
                        fontSize: '12px',
                        cursor: 'pointer',
                        marginLeft: '10px',
                      }}
                    >
                      <Edit2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '30px',
            backgroundColor: COLORS.background,
            borderRadius: '8px',
            textAlign: 'center',
            color: COLORS.textSecondary,
            fontSize: '13px',
          }}>
            Нет IDO токенов
          </div>
        )}
      </div>

      {/* Transactions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600', margin: 0 }}>
            История IDO
          </h4>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '6px 12px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '6px',
              color: '#000',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Plus size={14} /> Создать
          </button>
        </div>
        
        {data?.transactions?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {data.transactions.map((tx, idx) => (
              <div key={tx.id || idx} style={{
                padding: '10px 12px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: tx.type === 'buy' ? `${COLORS.success}15` : `${COLORS.danger}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {tx.type === 'buy' ? (
                      <ArrowDownCircle size={14} color={COLORS.success} />
                    ) : (
                      <ArrowUpCircle size={14} color={COLORS.danger} />
                    )}
                  </div>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '500' }}>
                      {tx.type === 'buy' ? 'Покупка' : 'Продажа'} ${tx.project_symbol || tx.ido_symbol}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {formatDate(tx.created_at)}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: tx.type === 'buy' ? COLORS.success : COLORS.danger, fontSize: '13px', fontWeight: '600' }}>
                      {tx.type === 'buy' ? '+' : '-'}{tx.token_amount?.toLocaleString()}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {tx.type === 'buy' ? '-' : '+'}${tx.usdt_amount?.toFixed(2)}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDeleteTx(tx.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: `${COLORS.danger}15`,
                      border: 'none',
                      borderRadius: '4px',
                      color: COLORS.danger,
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '30px',
            backgroundColor: COLORS.background,
            borderRadius: '8px',
            textAlign: 'center',
            color: COLORS.textSecondary,
            fontSize: '13px',
          }}>
            Нет транзакций IDO
          </div>
        )}
      </div>

      {/* Create Transaction Modal */}
      {showCreateModal && (
        <CreateIDOTransactionModal
          clientId={clientId}
          projects={data?.projects || []}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default IDOTab;
